<template>
    <div
        class="ComplianceWithCategoryResolutionReview"
        :class="{ 'ComplianceWithCategoryResolutionReview--globalScope': showGlobalScope }"
    >
        <div class="ComplianceWithCategoryResolutionReview__container">
            <div class="ComplianceWithCategoryResolutionReview__form">
                <GlobalActionScopeCard
                    v-if="showGlobalScope"
                    class="ComplianceWithCategoryResolutionReview__scopeCard"
                    :entity-count="affectedEntityCount"
                    field-label="category"
                    value-label="form number"
                    as-phrase="a match for the category shown"
                />
                <Table class="ComplianceWithCategoryResolutionReview__table">
                    <template #head>
                        <HeadCell
                            data-test-id="ComplianceWithCategoryResolutionReview__fieldHeader"
                            class="ComplianceWithCategoryResolutionReview__fieldHeader"
                        >
                            Category
                        </HeadCell>
                        <HeadCell data-test-id="ComplianceWithCategoryResolutionReview__valueHeader">
                            Form Number
                        </HeadCell>
                        <HeadCell
                            data-test-id="ComplianceWithCategoryResolutionReview__decisionHeader"
                            class="ComplianceWithCategoryResolutionReview__decisionHeader"
                        >
                            Decision
                        </HeadCell>
                    </template>
                    <Row hoverable>
                        <Cell
                            data-test-id="ComplianceWithCategoryResolutionReview__fieldRowCell"
                            class="ComplianceWithCategoryResolutionReview__fieldRowCell"
                        >
                            {{ actionDetails.category }}
                        </Cell>
                        <Cell data-test-id="ComplianceWithCategoryResolutionReview__valueRowCell">
                            {{ actionDetails.providedValue }}
                        </Cell>
                        <Cell class="ComplianceWithCategoryResolutionReview__decisionHeader">
                            <!-- A decided action shows what was decided in place of the controls. -->
                            <span
                                v-if="readOnly"
                                class="ComplianceWithCategoryResolutionReview__recordedDecision"
                            >
                                {{ recordedDecision }}
                            </span>
                            <RbacDisableDefaultGuard v-else :features="[RbacFeature.actionsResolveGlobalAction]">
                                <div class="ComplianceWithCategoryResolutionReview__decisionsContainer">
                                    <NewButton
                                        class="ComplianceWithCategoryResolutionReview__rejectButton"
                                        variant="outline"
                                        color="danger"
                                        @click="resolve(false)"
                                    >
                                        Reject
                                    </NewButton>
                                    <NewButton
                                        class="ComplianceWithCategoryResolutionReview__acceptButton"
                                        variant="outline"
                                        color="primary"
                                        @click="resolve(true)"
                                    >
                                        Accept
                                    </NewButton>
                                </div>
                            </RbacDisableDefaultGuard>
                        </Cell>
                    </Row>
                </Table>
            </div>

            <RbacDisplayGuard v-if="showGlobalScope" :features="[RbacFeature.actionsViewDocument]">
                <div class="ComplianceWithCategoryResolutionReview__documents">
                    <GlobalActionDocumentPanel :action-review="actionReview" />
                </div>
            </RbacDisplayGuard>
        </div>
    </div>
</template>

<script lang="ts">
    import { defineComponent, PropType } from 'vue';

    import { Cell, HeadCell, Row, Table } from '@evidentid/dashboard-commons/components/Table';
    import RbacDisableDefaultGuard
        from '@evidentid/dashboard-commons/modules/role-base-access-control/components/rbac-guard/RbacDisableDefaultGuard.vue';
    import RbacDisplayGuard
        from '@evidentid/dashboard-commons/modules/role-base-access-control/components/rbac-guard/RbacDisplayGuard.vue';
    import { typeNarrowing } from '@evidentid/dashboard-commons/utils/typeNarrowing';
    import {
        AcceptanceActionResolveInput,
        ActionReview,
        ActionType, EvaluatedEntityMappingActionDetails,
    } from '@evidentid/tprm-portal-lib/models/entity-actions-review';
    import { RbacFeature } from '@evidentid/tprm-portal-lib/models/rbac';

    import NewButton from '@/components/new-button/NewButton.vue';
    import GlobalActionDocumentPanel
        from '@/modules/entity-actions-review/components/GlobalActionDocumentPanel/GlobalActionDocumentPanel.vue';
    import GlobalActionScopeCard
        from '@/modules/entity-actions-review/components/GlobalActionScopeCard/GlobalActionScopeCard.vue';
    import { WRONG_ACTION_DETAILS_TYPE_MESSAGE } from '@/modules/entity-actions-review/constants';
    import { getAffectedEntityCount } from '@/modules/entity-actions-review/utils/globalActionScope';

    export default defineComponent({
        name: 'ComplianceWithCategoryResolutionReview',
        components: {
            RbacDisableDefaultGuard,
            RbacDisplayGuard,
            Row,
            Cell,
            Table,
            NewButton,
            GlobalActionDocumentPanel,
            GlobalActionScopeCard,
            HeadCell,
        },
        props: {
            actionReview: {
                type: Object as PropType<ActionReview>,
                required: true,
            },
            readOnly: {
                type: Boolean as PropType<boolean>,
                default: false,
            },
        },
        emits: [ 'resolve' ],
        data() {
            return {
                RbacFeature,
            };
        },
        computed: {
            recordedDecision(): string {
                const outcome = this.actionReview.resolution?.outcome;
                if (outcome === 'accepted') {
                    return 'Accepted';
                }
                return outcome === 'rejected' ? 'Rejected' : 'Not recorded';
            },
            actionDetails(): EvaluatedEntityMappingActionDetails {
                return typeNarrowing(
                    this.actionReview.action,
                    this.actionReview.action.$action === ActionType.complianceWithCategoryResolution,
                    WRONG_ACTION_DETAILS_TYPE_MESSAGE,
                );
            },
            affectedEntityCount(): number {
                return getAffectedEntityCount(this.actionReview.evaluationResults);
            },
            showGlobalScope(): boolean {
                return !this.readOnly && this.affectedEntityCount > 0;
            },
        },
        methods: {
            resolve(accepted: boolean): void {
                const data: AcceptanceActionResolveInput = {
                    $action: ActionType.complianceWithCategoryResolution,
                    accepted,
                };
                this.$emit('resolve', data);
            },
        },
    });
</script>
